import { useEffect, useState } from "react";
import { Settings2, ShieldCheck } from "lucide-react";

import {
  getTufMaintenancePolicy,
  setTufMaintenancePolicy,
  type TufMaintenancePolicy,
  type TufMaintenancePolicyInput,
} from "./keysTrustApi";
import { useKeysTrustManagedCopy } from "./keysTrustManagedCatalog";

type Field = keyof TufMaintenancePolicyInput;

const FIELDS: Field[] = [
  "root_validity_days",
  "targets_validity_days",
  "snapshot_validity_days",
  "timestamp_validity_days",
  "warning_days",
];

const FIELD_LABELS = {
  root_validity_days: "tufPolicyRoot",
  targets_validity_days: "tufPolicyTargets",
  snapshot_validity_days: "tufPolicySnapshot",
  timestamp_validity_days: "tufPolicyTimestamp",
  warning_days: "tufPolicyWarning",
} as const;

const FIELD_HELP = {
  root_validity_days: "tufPolicyRootHelp",
  targets_validity_days: "tufPolicyTargetsHelp",
  snapshot_validity_days: "tufPolicySnapshotHelp",
  timestamp_validity_days: "tufPolicyTimestampHelp",
  warning_days: "tufPolicyWarningHelp",
} as const;

function parsePolicyValues(
  values: Record<Field, string>,
): TufMaintenancePolicyInput | null {
  const parsed = Object.fromEntries(
    FIELDS.map((field) => [field, Number(values[field])]),
  ) as unknown as TufMaintenancePolicyInput;
  if (
    FIELDS.some((field) => !/^\d+$/.test(values[field]) || parsed[field] < 1 || parsed[field] > 3650)
    || !(parsed.root_validity_days > parsed.targets_validity_days
      && parsed.targets_validity_days > parsed.snapshot_validity_days
      && parsed.snapshot_validity_days > parsed.timestamp_validity_days
      && parsed.timestamp_validity_days > parsed.warning_days)
  ) {
    return null;
  }
  return parsed;
}

export function TufMaintenancePolicyEditor({
  compact = false,
  startEditing = false,
  onSaved,
  onSessionUnavailable,
}: {
  compact?: boolean;
  startEditing?: boolean;
  onSaved?: (policy: TufMaintenancePolicy) => void;
  onSessionUnavailable?: () => void;
}) {
  const { t } = useKeysTrustManagedCopy();
  const [policy, setPolicy] = useState<TufMaintenancePolicy | null | undefined>(undefined);
  const [editing, setEditing] = useState(startEditing);
  const [values, setValues] = useState<Record<Field, string>>({
    root_validity_days: "",
    targets_validity_days: "",
    snapshot_validity_days: "",
    timestamp_validity_days: "",
    warning_days: "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function populate(value: TufMaintenancePolicy | null) {
    setPolicy(value);
    setEditing(startEditing || value === null);
    if (value) {
      setValues({
        root_validity_days: String(value.root_validity_days),
        targets_validity_days: String(value.targets_validity_days),
        snapshot_validity_days: String(value.snapshot_validity_days),
        timestamp_validity_days: String(value.timestamp_validity_days),
        warning_days: String(value.warning_days),
      });
    }
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void getTufMaintenancePolicy()
      .then((value) => { if (active) populate(value); })
      .catch((reason) => {
        if (!active) return;
        const detail = reason instanceof Error ? reason.message : t("tufPolicyLoadError");
        if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
        setError(detail === "SESSION_UNAVAILABLE" ? t("tufPolicyLoadError") : detail);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [onSessionUnavailable, t, attempt]);

  function update(field: Field, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
  }

  const validValues = parsePolicyValues(values);

  async function save() {
    const parsed = parsePolicyValues(values);
    if (!parsed) {
      setError(t("tufPolicyValidation"));
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await setTufMaintenancePolicy(parsed);
      populate(saved);
      setMessage(t("tufPolicySaved", { version: saved.policy_version }));
      onSaved?.(saved);
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : t("tufPolicySaveError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={compact ? "tuf-policy-editor is-compact" : "tuf-policy-editor"} aria-label={t("tufPolicyAria")}>
      <header>
        <div>
          <strong>
            <Settings2 size={15} />
            {policy === null ? t("tufPolicyCreateTitle") : t("tufPolicyTitle")}
          </strong>
          <p>{policy === null ? t("tufPolicyCreateBody") : t("tufPolicyBody")}</p>
        </div>
        {policy && !editing && (
          <button type="button" className="admin-v12-secondary" onClick={() => setEditing(true)}>
            {t("tufPolicyEdit")}
          </button>
        )}
      </header>

      {error && <p className="admin-v12-form-error" role="alert">{error}</p>}
      {message && <p className="keys-trust-managed-success" role="status">{message}</p>}
      {loading && <p role="status">{t("loading")}</p>}
      {!loading && error && policy === undefined && <button type="button" className="admin-v12-secondary" onClick={() => setAttempt(value => value + 1)}>{t("refresh")}</button>}

      {policy === null && (
        <div className="tuf-policy-empty-state" role="status">
          <strong>{t("tufPolicyNotConfigured")}</strong>
          <p>{t("tufPolicyNotConfiguredBody")}</p>
          <small>{t("tufPolicyVersionPending")}</small>
        </div>
      )}

      {policy && !editing && (
        <div className="tuf-policy-summary">
          <ShieldCheck size={15} />
          <span>{t("tufPolicySummary", {
            root: policy.root_validity_days,
            targets: policy.targets_validity_days,
            snapshot: policy.snapshot_validity_days,
            timestamp: policy.timestamp_validity_days,
            warning: policy.warning_days,
          })}</span>
          <small>{t("tufPolicyVersion", { version: policy.policy_version })}</small>
        </div>
      )}

      {editing && policy !== undefined && (
        <>
          <div className="tuf-policy-fields">
            {FIELDS.map((field) => {
              const helpId = `tuf-policy-${field}-help`;
              return (
                <label key={field}>
                  <span>{t(FIELD_LABELS[field])}</span>
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    inputMode="numeric"
                    value={values[field]}
                    aria-describedby={helpId}
                    onChange={(event) => update(field, event.currentTarget.value)}
                  />
                  <small id={helpId}>{t(FIELD_HELP[field])}</small>
                </label>
              );
            })}
          </div>
          <p className={validValues ? "tuf-policy-rule is-valid" : "tuf-policy-rule"}>
            {validValues ? t("tufPolicyRuleSatisfied") : t("tufPolicyRule")}
          </p>
          <p className="tuf-policy-boundary">{t("tufPolicyNoGuess")}</p>
          <div className="tuf-policy-actions">
            {policy && <button type="button" disabled={busy} onClick={() => setEditing(false)}>{t("tufPolicyCancel")}</button>}
            <button
              type="button"
              className="admin-v12-primary"
              disabled={busy || !validValues}
              onClick={() => void save()}
            >
              {busy ? t("working") : policy === null ? t("tufPolicyCreate") : t("tufPolicySave")}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
